import React, { useCallback, useRef, useState } from "react"
import { Box, Typography, IconButton, Popover, CircularProgress } from "@mui/material"
import { AppDetailList, AppValueChip } from "@components"
import { reportFormNumSearches, reportFormData } from "Pages/ReportsAlerts/Reports/@signals"
import { CountUpSeeker, IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"
import { useReportBackendApiPath } from "Pages/ReportsAlerts/Reports/@hooks"
import { dsColorNew } from "@palette"


export default function ReportFormHeaderNumSearches() {
	const isDanger = reportFormNumSearches.value >= 1000
	const { backendUrl } = useReportBackendApiPath()
	const anchorRef = useRef(null)
	const [open, setOpen] = useState(false)
	const [info, setInfo] = useState(null)
	const [loading, setLoading] = useState(false)

	const handleOpen = useCallback(() => {
		setLoading(true)
		setOpen(true)
		postSubmit(`${backendUrl}/num-searches?isReportInfo=1`, reportFormData.value)
			.then(data => setInfo(data))
			.catch(() => setInfo(null))
			.finally(() => setLoading(false))
	}, [backendUrl])

	return <>
		<Box sx={{
			display: 'flex', alignItems: 'center', gap: 1.25, pl: 1.5, pr: 0.75, py: 0.5,
			border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px',
			color: (reportFormNumSearches.value > 0 ? isDanger ? dsColorNew.dangerRed : dsColorNew.successText : dsColorNew.black),
		}}>
			<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>Num. searches</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', fontSize: 18, fontWeight: 700 }}>
				<CountUpSeeker end={reportFormNumSearches.value}/>
				{!!isDanger && <IconSeeker icon="fire" sx={{ color: dsColorNew.dangerRed, fontSize: 16, px: 0.5 }}/>}
			</Box>
			<IconButton ref={anchorRef} data-tour-id="tour-num-searches-info" size="small" onClick={handleOpen} aria-label="How the searches are counted" sx={{ borderRadius: '7px', color: dsColorNew.textMuted }}>
				<IconSeeker icon="circle-info" sx={{ fontSize: 16 }}/>
			</IconButton>
		</Box>

		<Popover
			open={open}
			anchorEl={anchorRef.current}
			onClose={() => setOpen(false)}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			transformOrigin={{ vertical: 'top', horizontal: 'right' }}
		>
			<Box sx={{ width: 440, p: 2 }}>
				{loading
					? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24}/></Box>
					: info
						? <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
							<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
								{[
									{ label: 'Num. searches', value: info.numSearches, color: isDanger ? dsColorNew.dangerRed : dsColorNew.successText },
									{ label: 'Num. offices', value: info.numOffices },
									{ label: 'Per office', value: info.numSearchesPerOffice },
								].map(({ label, value, color }) => <Box key={label} sx={{ border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', px: 1.25, py: 1 }}>
									<Typography sx={{ fontSize: 11, fontWeight: 600, color: dsColorNew.textSubtle, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</Typography>
									<Typography sx={{ fontSize: 18, fontWeight: 700, color: color || dsColorNew.textTitle }}>{value ?? '—'}</Typography>
								</Box>)}
							</Box>
							<AppDetailList labelWidth={130} items={[
								{ label: 'First pickup date', value: info.firstPickupDate ?? '—' },
								{ label: 'Last pickup date', value: info.lastPickupDate ?? '—' },
								{
									label: 'Pickup dates',
									value: info.pickupDates?.length
										? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 180, overflowY: 'auto' }}>
											{info.pickupDates.map(d => <AppValueChip key={d} tone="accent">{d}</AppValueChip>)}
										</Box>
										: '—',
								},
							]}/>
						</Box>
						: <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>No data available</Typography>
				}
			</Box>
		</Popover>
	</>
}